import { API_BASE_URL as API_BASE } from '../config/api';
import { createAuthenticatedApiClient } from './authenticatedApiClient';
import { getStoredUserId } from './authSession';
import {
  buildUserScopedQuery,
  mapSupplierApiError,
  resolveSupplierUserId,
  toSupplierUpdatePatch,
  withUserScopedBody,
} from './supplierUserScope';
import {
  createApiResponse,
  createCompany,
  createCompanyInfo,
  createRiskAnalysis,
  createNewsArticle,
  createCompanyRiskSummary,
  createCompanyStatistics
} from '../models';
import {
  clearEtagEntry,
  etagKeyForUser,
  getEtagEntry,
  setEtagEntry,
} from './etagStore';

const API_BASE_URL = `${API_BASE}/api/v1/companies`;
const COMPANIES_SUMMARY_ETAG_RESOURCE = 'companies-summary';

const apiClient = createAuthenticatedApiClient({ baseURL: API_BASE_URL });

export const companyService = {
  async getCompaniesList(params = {}) {
    try {
      const {
        page,
        size,
        sortBy,
        sortDirection,
        search,
        industry,
        region,
        view,
        fields,
        updatedSince,
      } = params;
      const requestParams = {};
      if (page != null) requestParams.page = page;
      if (size != null) requestParams.size = size;
      if (sortBy) requestParams.sortBy = sortBy;
      if (sortDirection) requestParams.sortDirection = sortDirection;
      if (search) requestParams.search = search;
      if (industry) requestParams.industry = industry;
      if (region) requestParams.region = region;
      if (view) requestParams.view = view;
      if (fields) requestParams.fields = fields;
      if (updatedSince) requestParams.updatedSince = updatedSince;

      const response = await apiClient.get('/', { params: requestParams });
      const apiResponse = createApiResponse(response.data);
      if (apiResponse.success && apiResponse.data) {
        if (Array.isArray(apiResponse.data)) {
          apiResponse.data = apiResponse.data.map((company) => createCompany(company));
        } else {
          apiResponse.data = createCompany(apiResponse.data);
        }
      }
      return apiResponse;
    } catch (error) {
      return createApiResponse({
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to fetch companies',
      });
    }
  },

  // Company Management
  async getAllCompanies() {
    return this.getCompaniesList();
  },

  async getCompaniesSummary(params = {}) {
    const userId = getStoredUserId();
    const etagKey = etagKeyForUser(COMPANIES_SUMMARY_ETAG_RESOURCE, userId || 'anonymous');
    const stored = params.etag != null ? { etag: params.etag } : getEtagEntry(etagKey);

    try {
      const {
        page = 1,
        size = 20,
        q,
        tier,
        industry,
        updatedSince,
        view,
      } = params;

      const requestParams = { page, size };
      if (q) requestParams.q = q;
      if (tier) requestParams.tier = tier;
      if (industry && industry !== 'all') requestParams.industry = industry;
      if (updatedSince) requestParams.updatedSince = updatedSince;
      if (view) requestParams.view = view;

      const headers = {};
      if (stored?.etag) headers['If-None-Match'] = stored.etag;

      const response = await apiClient.get('/summary', {
        params: requestParams,
        headers,
        validateStatus: (status) => (status >= 200 && status < 300) || status === 304,
      });

      const responseEtag = response.headers?.etag ?? stored?.etag ?? '';

      if (response.status === 304) {
        return createApiResponse({
          success: true,
          notModified: true,
          etag: responseEtag,
        });
      }

      const body = response.data ?? {};
      const content = Array.isArray(body.content) ? body.content : [];
      const pagination = body.pagination ?? null;

      setEtagEntry(etagKey, { etag: responseEtag });

      return createApiResponse({
        success: body.success !== false,
        data: content.map((company) => createCompany(company)),
        pagination,
        etag: responseEtag,
        message: body.message,
      });
    } catch (error) {
      if (error.response?.status === 404) {
        return this.getCompaniesList(params);
      }
      return createApiResponse({
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to fetch company summaries',
      });
    }
  },

  invalidateCompaniesSummaryCache(userId) {
    clearEtagEntry(etagKeyForUser(COMPANIES_SUMMARY_ETAG_RESOURCE, userId || 'anonymous'));
  },

  /**
   * Get company by ticker. With userId, returns global company or this user's user-added company.
   * GET /companies/{ticker} or /companies/{ticker}?userId={userId}
   */
  async getCompanyByTicker(ticker, userId) {
    try {
      const params = userId ? { userId } : {};
      const response = await apiClient.get(`/${ticker}`, { params });
      const apiResponse = createApiResponse(response.data);
      if (apiResponse.success && apiResponse.data) {
        apiResponse.data = createCompany(apiResponse.data);
      }
      return apiResponse;
    } catch (error) {
      return createApiResponse({
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to fetch company'
      });
    }
  },

  async searchCompanies(query) {
    try {
      const response = await apiClient.get(`/search?q=${encodeURIComponent(query)}`);
      const apiResponse = createApiResponse(response.data);
      if (apiResponse.success && apiResponse.data) {
        if (Array.isArray(apiResponse.data)) {
          apiResponse.data = apiResponse.data.map(company => createCompany(company));
        } else {
          apiResponse.data = createCompany(apiResponse.data);
        }
      }
      return apiResponse;
    } catch (error) {
      return createApiResponse({
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to search companies'
      });
    }
  },

  async getCompaniesByIndustry(industry) {
    try {
      const response = await apiClient.get(`/industry/${industry}`);
      const apiResponse = createApiResponse(response.data);
      if (apiResponse.success && apiResponse.data) {
        if (Array.isArray(apiResponse.data)) {
          apiResponse.data = apiResponse.data.map(company => createCompany(company));
        } else {
          apiResponse.data = createCompany(apiResponse.data);
        }
      }
      return apiResponse;
    } catch (error) {
      return createApiResponse({
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to fetch companies by industry'
      });
    }
  },

  async getCompaniesByRiskCategory(riskCategory) {
    try {
      const response = await apiClient.get(`/risk-category/${riskCategory}`);
      const apiResponse = createApiResponse(response.data);
      if (apiResponse.success && apiResponse.data) {
        if (Array.isArray(apiResponse.data)) {
          apiResponse.data = apiResponse.data.map(company => createCompany(company));
        } else {
          apiResponse.data = createCompany(apiResponse.data);
        }
      }
      return apiResponse;
    } catch (error) {
      return createApiResponse({
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to fetch companies by risk category'
      });
    }
  },

  async getCompanyStatistics() {
    try {
      const response = await apiClient.get('/statistics');
      const apiResponse = createApiResponse(response.data);
      if (apiResponse.success && apiResponse.data) {
        apiResponse.data = createCompanyStatistics(apiResponse.data);
      }
      return apiResponse;
    } catch (error) {
      return createApiResponse({
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to fetch company statistics'
      });
    }
  },

  async getCompanyRiskSummary(ticker) {
    try {
      const response = await apiClient.get(`/${ticker}/risk-summary`);
      const apiResponse = createApiResponse(response.data);
      if (apiResponse.success && apiResponse.data) {
        apiResponse.data = createCompanyRiskSummary(apiResponse.data);
      }
      return apiResponse;
    } catch (error) {
      return createApiResponse({
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to fetch company risk summary'
      });
    }
  },

  // Company Details and Analysis
  async getCompanyDetails(ticker) {
    try {
      const response = await apiClient.get(`/${ticker}/details`);
      const apiResponse = createApiResponse(response.data);
      if (apiResponse.success && apiResponse.data) {
        apiResponse.data = createCompany(apiResponse.data);
      }
      return apiResponse;
    } catch (error) {
      return createApiResponse({
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to fetch company details'
      });
    }
  },

  async getCompanyRiskAnalysis(ticker) {
    try {
      const response = await apiClient.get(`/${ticker}/risk-analysis`);
      const apiResponse = createApiResponse(response.data);
      if (apiResponse.success && apiResponse.data) {
        apiResponse.data = createRiskAnalysis(apiResponse.data);
      }
      return apiResponse;
    } catch (error) {
      return createApiResponse({
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to fetch company risk analysis'
      });
    }
  },

  async getCompanyNews(ticker, limit = 10) {
    try {
      const response = await apiClient.get(`/${ticker}/news?limit=${limit}`);
      const apiResponse = createApiResponse(response.data);
      let articles = apiResponse.success && apiResponse.data != null
        ? (Array.isArray(apiResponse.data) ? apiResponse.data : [apiResponse.data])
        : Array.isArray(response.data) ? response.data : (response.data?.data && Array.isArray(response.data.data) ? response.data.data : []);
      articles = articles.map(article => createNewsArticle(article));
      return createApiResponse({ success: true, data: articles });
    } catch (error) {
      return createApiResponse({
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to fetch company news'
      });
    }
  },

  async getCompanyFinancials(ticker) {
    try {
      const response = await apiClient.get(`/${ticker}/financials`);
      const apiResponse = createApiResponse(response.data);
      // Note: Financials might be a different model type, keeping as raw data for now
      return apiResponse;
    } catch (error) {
      return createApiResponse({
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to fetch company financials'
      });
    }
  },

  async getCompanySupplyChain(ticker) {
    try {
      const response = await apiClient.get(`/${ticker}/supply-chain`);
      const apiResponse = createApiResponse(response.data);
      // Note: Supply chain might be a different model type, keeping as raw data for now
      return apiResponse;
    } catch (error) {
      return createApiResponse({
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to fetch supply chain data'
      });
    }
  },

  // Company Create, Update, Delete Operations
  /**
   * Create supplier for the logged-in user.
   * POST /companies?userId= — returns canonical scoped ticker in data.ticker.
   */
  async createCompany(companyData, userId) {
    const uid = resolveSupplierUserId(userId);
    if (!uid) {
      return createApiResponse({ success: false, message: 'User not authenticated' });
    }
    try {
      const payload = withUserScopedBody(
        { ...companyData, is_user_made: true },
        uid
      );
      const response = await apiClient.post('/', payload, {
        params: buildUserScopedQuery(uid),
      });

      const apiResponse = createApiResponse(response.data);
      if (apiResponse.success && apiResponse.data) {
        apiResponse.data = createCompany(apiResponse.data);
      }
      return apiResponse;
    } catch (error) {
      return mapSupplierApiError(error, 'Failed to create company');
    }
  },

  /**
   * Update supplier for the logged-in user (private user_companies doc).
   * PUT /companies/{ticker}?userId= — never use without userId for end-user edits.
   */
  async updateCompany(ticker, updates, userId) {
    const uid = resolveSupplierUserId(userId);
    if (!uid) {
      return createApiResponse({ success: false, message: 'User not authenticated' });
    }
    if (!ticker) {
      return createApiResponse({ success: false, message: 'Ticker is required' });
    }
    try {
      const patch = toSupplierUpdatePatch(updates, ticker);
      const payload = withUserScopedBody(patch, uid);
      const response = await apiClient.put(`/${encodeURIComponent(patch.ticker || ticker)}`, payload, {
        params: buildUserScopedQuery(uid),
      });

      const apiResponse = createApiResponse(response.data);
      if (apiResponse.success && apiResponse.data) {
        apiResponse.data = createCompany(apiResponse.data);
      }
      return apiResponse;
    } catch (error) {
      return mapSupplierApiError(error, 'Failed to update company');
    }
  },

  async deleteCompany(ticker) {
    try {
      const response = await apiClient.delete(`/${ticker}`);
      return createApiResponse(response.data);
    } catch (error) {
      return createApiResponse({
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to delete company'
      });
    }
  },

  // User-Created Companies
  async getUserMadeCompanies() {
    try {
      const response = await apiClient.get('/user-made');
      const apiResponse = createApiResponse(response.data);
      if (apiResponse.success && apiResponse.data) {
        if (Array.isArray(apiResponse.data)) {
          apiResponse.data = apiResponse.data.map(company => createCompany(company));
        }
      }
      return apiResponse;
    } catch (error) {
      return createApiResponse({
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to fetch user-made companies'
      });
    }
  },

  async getCompaniesByUser(userId) {
    try {
      const response = await apiClient.get(`/user/${userId}`);
      const apiResponse = createApiResponse(response.data);
      if (apiResponse.success && apiResponse.data) {
        if (Array.isArray(apiResponse.data)) {
          apiResponse.data = apiResponse.data.map(company => createCompany(company));
        }
      }
      return apiResponse;
    } catch (error) {
      return createApiResponse({
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to fetch user companies'
      });
    }
  }
};
